import { AddPhotoAlternate, DeleteOutline } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';

import { translate } from '@/ui/i18n';

import { UploadImageService } from './FSImageField.utils/upload-image-service';
import { UploadImageServiceFake } from './FSImageField.utils/upload-image-service-fake';

export function FSImageField({
  name,
  value,
  onChange,
  uploadImageService = new UploadImageServiceFake(),
}: {
  name: string;
  value?: string; // empty ' = no image
  onChange: (fileUrl: string) => void;
  uploadImageService?: UploadImageService;
}) {
  const [isUploading, setIsUploading] = useState(false);

  const onInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setIsUploading(true);
      const uploadedImageUrl = await uploadImageService.upload(file);
      setIsUploading(false);
      onChange(uploadedImageUrl);
    }
  };

  const onClickRemoveImage = () => {
    onChange('');
  };

  if (isUploading) {
    return <IsUploadingView />;
  }

  const imageNotSet = value === undefined || value === '';
  if (imageNotSet) {
    return <ClickToUploadView name={name} onInputChange={onInputChange} />;
  }

  return (
    <ImageUploadedView image={value} onClickRemoveImage={onClickRemoveImage} />
  );
}

function ClickToUploadView({
  name,
  onInputChange,
}: {
  name: string;
  onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <ImageComponentWrapper>
      <input
        id={`${name}-file-input`}
        type="file"
        accept="image/*"
        onChange={onInputChange}
        style={{ display: 'none' }}
      />
      <label htmlFor={`${name}-file-input`}>
        <Box sx={{ cursor: 'pointer' }}>
          <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
            <AddImageButton />
            <AddImageText />
          </Stack>
        </Box>
      </label>
    </ImageComponentWrapper>
  );
}

function AddImageButton() {
  return (
    <Box
      sx={{
        width: '3.5rem',
        height: '3.5rem',
        p: 1,
        backgroundColor: 'rgba(255, 239, 230, 0.08)',
        borderRadius: '20%',
      }}
    >
      <Stack
        sx={{
          width: '100%',
          height: '100%',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <AddPhotoAlternate />
      </Stack>
    </Box>
  );
}

function AddImageText() {
  const theme = useTheme();
  return (
    <Typography sx={{ color: theme.palette.primary.main, fontWeight: 'bold' }}>
      {translate('Form.add-image')}
    </Typography>
  );
}

function ImageUploadedView({
  image,
  onClickRemoveImage,
}: {
  image: string;
  onClickRemoveImage: () => void;
}) {
  return (
    <ImageComponentWrapper>
      <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 2 }}>
        <PreviewImage image={image} />
        <RemoveImageButton onClickRemoveImage={onClickRemoveImage} />
      </Stack>
    </ImageComponentWrapper>
  );
}

function PreviewImage({ image }: { image: string }) {
  return (
    <Box
      sx={{
        width: '3.5rem',
        height: '3.5rem',
        p: 1,
        backgroundColor: 'rgba(255, 239, 230, 0.08)',
        borderRadius: '20%',
      }}
    >
      <img
        src={image}
        alt="Selected"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
    </Box>
  );
}

function RemoveImageButton({
  onClickRemoveImage,
}: {
  onClickRemoveImage: () => void;
}) {
  return (
    <IconButton onClick={onClickRemoveImage}>
      <DeleteOutline />
    </IconButton>
  );
}

function IsUploadingView() {
  return (
    <ImageComponentWrapper>
      <Box
        sx={{
          my: 1,
          width: '3.5rem',
          height: '3.5rem',
          backgroundColor: 'rgba(255, 239, 230, 0.08)',
          borderRadius: '20%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size="1rem" />
      </Box>
    </ImageComponentWrapper>
  );
}

function ImageComponentWrapper({ children }: { children: React.ReactNode }) {
  return <Box sx={{ my: 1 }}>{children}</Box>;
}
